import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchSessionConfig } from '../../services/api';

/**
 * MicrosoftCallback — handles the post-Microsoft-OAuth browser redirect.
 *
 * The backend /auth/microsoft/callback endpoint validates the OAuth tokens,
 * provisions the user, then redirects the browser here with:
 *   ?access_token=<jwt>&refresh_token=<token>&redirect=<path>
 * OR on error:
 *   ?error=<code>&message=<description>
 *
 * This component stores the tokens and bootstraps the session,
 * then navigates to the intended destination.
 */
const MicrosoftCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const handleCallback = async () => {
      const error = searchParams.get('error');
      const errorMessage = searchParams.get('message');
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const redirectTo = searchParams.get('redirect') || '/dashboard';

      // ── Error returned by Microsoft or backend ──────────────────────────
      if (error) {
        const friendly = getFriendlyError(error, errorMessage);
        sessionStorage.setItem('sessionMessage', friendly);
        navigate('/login', { replace: true });
        return;
      }

      // ── Tokens missing ──────────────────────────────────────────────────
      if (!accessToken || !refreshToken) {
        sessionStorage.setItem('sessionMessage', 'Authentication failed. Please try again.');
        navigate('/login', { replace: true });
        return;
      }

      // ── Store tokens ────────────────────────────────────────────────────
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // ── Fetch user profile and session config ───────────────────────────
      try {
        // Dynamically import to avoid circular deps
        const api = (await import('../../services/api')).default;
        const userResponse = await api.get('/auth/me');
        localStorage.setItem('user', JSON.stringify(userResponse.data));

        // Bootstrap Redux auth state — loginWithTokens sets isAuthenticated: true
        // so ProtectedRoute allows navigation to the target page
        const { loginWithTokens } = await import('../../store/slices/authSlice');
        await dispatch(loginWithTokens());

        // Session manager is initialized by App.js when isAuthenticated changes;
        // pre-fetch the config so it's cached for that effect
        await fetchSessionConfig();

        // Clear any stored redirect now that we are logged in
        sessionStorage.removeItem('postLoginRedirect');

        // Navigate to intended destination
        navigate(redirectTo, { replace: true });
      } catch (err) {
        // Token is valid but fetching user failed — still continue
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        sessionStorage.setItem('sessionMessage', 'Login succeeded but could not load your profile. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    handleCallback();
  }, [searchParams, navigate, dispatch]); // stable refs — won't cause re-runs

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
      <svg width="48" height="48" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
      </svg>
      <p style={{ color: 'var(--color-text-secondary, #5e6c84)', fontSize: '15px' }}>
        Completing sign-in with Microsoft...
      </p>
      <span className="spinner" style={{ width: '24px', height: '24px' }} />
    </div>
  );
};

/**
 * Map raw OAuth error codes to user-friendly messages.
 */
function getFriendlyError(code, detail) {
  const map = {
    access_denied:        detail || 'Access denied. You are not authorised to access this application.',
    account_deactivated:  detail || 'Your account has been deactivated. Please contact your administrator for access.',
    invalid_state:        'Login session expired. Please try again.',
    token_exchange_failed:'Authentication failed. Please try again.',
    token_invalid:        'Could not verify your Microsoft identity. Please try again.',
    no_id_token:          'Microsoft did not return an identity token. Please try again.',
    missing_params:       'Incomplete login response. Please try again.',
    invalid_user_info:    'Could not read your Microsoft profile. Please try again.',
  };
  return map[code] || detail || 'An unexpected error occurred during sign-in.';
}

export default MicrosoftCallback;
